/**
 * WI-B-04 — the mounted auth boundary. Wraps the App shell in AuthGate so the
 * shell renders only after a session is established (never under a baked pilot
 * bearer — main.tsx leak). Selects the dev stub driver behind VITE_MARK_AUTH_STUB;
 * otherwise the LIVE cookie-mode gate (Option 1, WI-B-08).
 *
 * Two live-vs-dev paths:
 *   - VITE_MARK_AUTH_STUB=1 (dev): the fixture magic-link stub driver + AuthGate,
 *     so the whole flow clicks through with `npm run dev`, no live WorkOS.
 *   - flag off (prod): CookieAuthGate — on load it probes GET /auth/session
 *     (the sealed httpOnly cookie the coordinator minted); a live session mounts
 *     the shell directly, no token ever in JS. Sign-in redirects to /auth/login.
 */

import { useEffect, useMemo, useState } from "preact/hooks";
import { App } from "../app";
import { AuthGate, type AuthDriver } from "./AuthGate";
import { makeStubAuthDriver, isAuthStubEnabled } from "../dev/stub-auth-driver";
import { getOutboxByState } from "../outbox/db";
import { AuthLaunch, AuthCallback, AuthError, AuthNoProjects } from "./AuthScreens";
import { getSession, installSession } from "../auth/session-store";
import { sessionOwnerPrincipal } from "../auth/workos-session";
import { startOutboxRuntime, stopOutboxRuntime } from "../outbox/runtime";
import {
  beginCookieLogin,
  bootstrapCookieSession,
  logoutCookieSession,
  type CookieBootstrap,
} from "../auth/workos-cookie-session";
import type { EstablishedSession } from "../auth/workos-session";
import type { AuthErrorKind } from "../auth/workos-callback";
import { installAuthenticatedBackGuard } from "../auth/authenticated-history";
import { markSessionStale } from "../auth/session-stale";

const AUTH_OWNER_CHANNEL = "mark-auth-owner-v1";

function broadcastAuthOwner(owner: string | null): void {
  if (typeof BroadcastChannel === "undefined") return;
  const channel = new BroadcastChannel(AUTH_OWNER_CHANNEL);
  channel.postMessage(owner);
  channel.close();
}

/** The mark-spin keyframe the A2/A4 spinner needs, injected once (the reference
 * DOM defined it in a <style> block; in the app we own it here). */
function ensureSpinKeyframe(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById("mark-spin-kf")) return;
  const style = document.createElement("style");
  style.id = "mark-spin-kf";
  style.textContent = "@keyframes mark-spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(style);
}

async function queuedCount(): Promise<number> {
  try {
    const session = getSession();
    if (!session) return 0;
    const owner = sessionOwnerPrincipal(session);
    return (await getOutboxByState("queued")).filter((row) => row.owner_principal === owner).length;
  } catch {
    return 0;
  }
}

export function AuthedApp() {
  ensureSpinKeyframe();
  if (isAuthStubEnabled()) return <StubAuthedApp />;
  return <CookieAuthedApp />;
}

/** Dev path: the fixture stub driver through the full AuthGate state machine. */
function StubAuthedApp() {
  const driver = useMemo<AuthDriver>(() => makeStubAuthDriver(promptEmail, queuedCount), []);
  return (
    <AuthGate driver={driver}>
      <AuthenticatedRuntimeApp />
    </AuthGate>
  );
}

type CookiePhase =
  | { name: "probing" }
  | { name: "unauth" }
  | { name: "not-provisioned"; display: string }
  | { name: "error"; kind: AuthErrorKind };

function consumeCookieAuthError(): AuthErrorKind | null {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const marker = url.searchParams.get("auth");
  const kinds: Record<string, AuthErrorKind> = {
    failed: "failed",
    cancelled: "cancelled",
    denied: "denied",
    expired: "expired",
    invalid: "invalid",
    superseded: "superseded",
    "rate-limited": "rate-limited",
  };
  if (!marker || !kinds[marker]) return null;
  url.searchParams.delete("auth");
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
  return kinds[marker];
}

/**
 * Live path (Option 1). On mount, probe the sealed-cookie session. A live session
 * mounts the shell straight away (the cookie authorises every request); otherwise
 * show A1 launch, whose Sign-in hands off to the coordinator's /auth/login.
 */
export function CookieAuthedApp() {
  const [callbackError] = useState<AuthErrorKind | null>(() => consumeCookieAuthError());
  const [session, setSession] = useState<EstablishedSession | null>(null);
  const [phase, setPhase] = useState<CookiePhase>(
    callbackError ? { name: "error", kind: callbackError } : { name: "probing" },
  );
  const [queued, setQueued] = useState(0);

  useEffect(() => {
    let live = true;
    void bootstrapCookieSession().then((r: CookieBootstrap) => {
      if (!live) return;
      if (r.kind === "session") {
        installSession(r.session);
        setSession(r.session);
      } else if (r.kind === "not-provisioned") {
        setPhase({ name: "not-provisioned", display: r.display });
      } else if (r.kind === "error") {
        void queuedCount().then((n) => live && setQueued(n));
        setPhase({ name: "error", kind: callbackError ?? "network" });
      } else {
        setPhase(callbackError ? { name: "error", kind: callbackError } : { name: "unauth" });
      }
    });
    return () => {
      live = false;
    };
    // callbackError is read once at mount (useState with no setter), so listing it
    // keeps this a run-once bootstrap.
  }, [callbackError]);

  if (session) {
    return (
      <AuthGate
        driver={makeRedirectOnlyDriver()}
        initialReauth={false}
        establishedSession={session}
      >
        <AuthenticatedRuntimeApp onSignOut={() => void logoutCookieSession()} />
      </AuthGate>
    );
  }

  switch (phase.name) {
    case "probing":
      return <AuthCallback />;
    case "not-provisioned":
      return (
        <AuthNoProjects
          email={phase.display}
          onCheckAgain={() => beginCookieLogin()}
          onSignOut={() => void logoutCookieSession()}
        />
      );
    case "error":
      return (
        <AuthError
          kind={phase.kind}
          queuedCount={queued}
          onRetry={() =>
            phase.kind === "network" ? window.location.reload() : beginCookieLogin()
          }
          onKeepCapturing={() => setPhase({ name: "unauth" })}
        />
      );
    case "unauth":
    default:
      return <AuthLaunch onSignIn={() => beginCookieLogin()} reauth={false} />;
  }
}

/** Mount network delivery only inside an established authenticated session.
 * Cleanup aborts any in-flight owner-scoped send before logout or actor change. */
function AuthenticatedRuntimeApp({ onSignOut }: { onSignOut?: () => void } = {}) {
  const session = getSession();
  useEffect(() => {
    if (!session) return;
    const owner = sessionOwnerPrincipal(session);
    const channel = typeof BroadcastChannel === "undefined"
      ? null
      : new BroadcastChannel(AUTH_OWNER_CHANNEL);
    if (channel) {
      channel.onmessage = (event) => {
        if (event.data === owner) return;
        stopOutboxRuntime();
        markSessionStale();
      };
      channel.postMessage(owner);
    }
    void startOutboxRuntime(owner).catch((err) => {
      // Capture remains durable in IDB even when delivery cannot start.
      console.error("[mark] authenticated outbox runtime failed to start:", err);
    });
    return () => {
      channel?.close();
      stopOutboxRuntime();
    };
  }, [session]);
  useEffect(() => {
    if (!session) return;
    return installAuthenticatedBackGuard();
  }, [session]);
  const signOut = onSignOut
    ? () => {
        broadcastAuthOwner(null);
        stopOutboxRuntime();
        onSignOut();
      }
    : undefined;
  return <App onSignOut={signOut} />;
}

/** Once a cookie session is live, the shell is mounted through AuthGate purely to
 * keep the A7 session-stale banner + re-auth chrome. Its driver only needs a
 * begin-login (redirect) + queuedCount; the token/exchange fns are never reached
 * because a session is already installed. */
function makeRedirectOnlyDriver(): AuthDriver {
  return {
    async beginLogin() {
      beginCookieLogin();
      return { error: "redirecting" }; // unreached — the page is replaced
    },
    exchange: async () => null,
    fetchScope: async () => ({ tenant_id: "", project_ids: [], bearer: "" }),
    checkMembership: () => null,
    queuedCount,
  };
}

/** Dev-only: collect the "magic link" email. A prompt keeps the stub zero-UI;
 * the seeded addresses are listed so Red knows what to type. Empty → admin. */
function promptEmail(): string {
  const seeded = "admin@mercer.test · super@mercer.test · member@mercer.test · nobody@mercer.test · ghost@nowhere.test";
  const v = typeof window !== "undefined" ? window.prompt(`[dev stub] sign in as:\n${seeded}`, "admin@mercer.test") : null;
  return v && v.trim() ? v : "admin@mercer.test";
}
